import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { clsx } from 'clsx';

import { capitalize } from '../../../../../core/utils/string.js';
import { buildImpressionUIElementAttributes } from '../../../../../core/mandatory-integrations/pulse/event/input-builders/impression/build-impression-ui-element-attributes.js';
import { usePulseContext } from '../../../../../core/mandatory-integrations/pulse/PulseProvider.js';

export const WordExplainer: React.FC<{ term: string; explanation: string }> = ({
  term,
  explanation,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  const termLowerCased = term.toLowerCase();

  const { trackClickUIElement } = usePulseContext();

  const trackToggle = useCallback((): void => {
    const action = isOpen ? 'close' : 'open';

    trackClickUIElement(
      {
        object: {
          id: 'word-explainer',
          name: 'toggle word explainer',
          elementType: 'Button',
          custom: {
            term: termLowerCased,
            action,
          },
        },
      },
      {
        object: {
          objectId: `word-explainer-${termLowerCased}`,
          name: `${action} word explainer`,
          elementType: 'Button',
        },
        placement: { containerName: 'page' },
      },
    );
  }, [isOpen, termLowerCased, trackClickUIElement]);

  const toggle = (): void => {
    trackToggle();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleDocClick = (event: MouseEvent): void => {
      const target = event.target as Node;

      if (
        buttonRef.current?.contains(target) ||
        tooltipRef.current?.contains(target)
      ) {
        return;
      }

      if (isOpen) {
        trackToggle();
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleDocClick);

    return () => {
      document.removeEventListener('click', handleDocClick);
    };
  }, [isOpen, trackToggle]);

  return (
    <dl
      className="word-explainer"
      data-pulse-entity-id={`word-explainer-${termLowerCased}`}
      data-pulse-entity-name="Word explainer"
      data-pulse-entity-type="UIElement"
      {...buildImpressionUIElementAttributes({
        object: {
          objectId: `word-explainer-${termLowerCased}`,
          name: 'Word explainer',
          elementType: 'Button',
        },
        placement: {
          containerName: 'page',
          foldPosition: 'Unknown',
        },
      })}
    >
      <dt>
        <button
          ref={buttonRef}
          onClick={toggle}
          aria-expanded={isOpen}
          aria-describedby={isOpen ? tooltipId : undefined}
          className={clsx('word-explainer__trigger', {
            'word-explainer__trigger--active': isOpen,
          })}
        >
          {term}
        </button>
      </dt>
      {isOpen && (
        <dd
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          hidden={!isOpen}
          aria-hidden={!isOpen}
          className="word-explainer__tooltip"
        >
          <div className="word-explainer__tooltip-header">
            <span className="word-explainer__tooltip-term">
              {capitalize(term)}:
            </span>
            <button
              type="button"
              onClick={toggle}
              aria-label={`Lukk definisjon av ${termLowerCased}`}
              className="word-explainer__tooltip-close"
            />
          </div>
          <div className="word-explainer__tooltip-explanation">
            {explanation}
          </div>
        </dd>
      )}
    </dl>
  );
};
